/**
 * Conteúdo da aba Contratos no relatório.
 * Exibe filtros próprios e tabela de contratos com ordenação e paginação.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Skeleton,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  MenuItem,
  Button,
  Grid,
  InputAdornment,
  IconButton,
  Collapse,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Search, Clear, FilterList, ExpandMore, ExpandLess, PictureAsPdf } from '@mui/icons-material';
import { Contract } from '../../types';
import { formatarData, formatarValor } from '../../utils/formatToView';
import { useRelatorioCores, getStatusChip, parseYmdToLocalDate, dateToYmd, fmtInt } from './relatorioUtils';
import { FiltersContratos, estadosBR } from './relatorioTypes';
import SysDateRangePicker from '../../components/SysDateRangePicker';
import FuncionariosSelect from '../../components/selects/FuncionariosSelect';
import PlanosSelect from '../../components/selects/PlanosSelect';

export interface RelatorioContratosTabProps {
  data: Contract[];
  filters: FiltersContratos;
  onFilterChange: (updates: Partial<FiltersContratos>) => void;
  onClearFilters: () => void;
  isLoading: boolean;
  page: number;
  rowsPerPage: number;
  orderBy: string;
  orderDir: 'asc' | 'desc';
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSort: (col: string) => void;
  sortData: <T>(data: T[]) => T[];
  paginateData: <T>(data: T[]) => T[];
  onExportPdf?: (ids: number[]) => Promise<void>;
}

const RelatorioContratosTab: React.FC<RelatorioContratosTabProps> = ({
  data,
  filters,
  onFilterChange,
  onClearFilters,
  isLoading,
  page,
  rowsPerPage,
  orderBy,
  orderDir,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  sortData,
  paginateData,
  onExportPdf,
}) => {
  const navigate = useNavigate();
  const [exportingPdf, setExportingPdf] = React.useState(false);
  const handleExportPdf = async () => {
    if (!onExportPdf || data.length === 0) return;
    setExportingPdf(true);
    try {
      await onExportPdf(data.map((c) => c.id));
    } finally {
      setExportingPdf(false);
    }
  };
  const cores = useRelatorioCores();
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const hasActiveFilters = Object.values(filters).some((v) => v !== '');
  const sorted = sortData(data);
  const rows = paginateData(sorted);

  const mostrarColunaQtdeFaturas = filters.inadimplente;
  const columns = [
    { id: 'id', label: 'ID', width: 60 },
    { id: 'titulo', label: 'Título' },
    { id: 'cliente', label: 'Cliente' },
    { id: 'vendedor', label: 'Vendedor', width: 120 },
    { id: 'status', label: 'Status', width: 120 },
    ...(mostrarColunaQtdeFaturas ? [{ id: 'qtde_faturas_vencidas', label: 'Qtde Faturas', width: 90 }] : []),
    { id: 'cidade_inst', label: 'Cidade Inst.' },
    { id: 'estado_inst', label: 'UF', width: 50 },
    { id: 'data_contrato', label: 'Data Contrato', width: 110 },
    { id: 'dia_vencimento', label: 'Dia Vcto', width: 70 },
    { id: 'valor_contratado', label: 'Valor', width: 100 },
  ];

  return (
    <Box>
      <Paper elevation={2} sx={{ mb: 1, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, bgcolor: alpha(cores.ativo, 0.04), cursor: 'pointer' }} onClick={() => setFiltersOpen(!filtersOpen)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList sx={{ fontSize: 18, color: cores.ativo }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>Filtros</Typography>
            {hasActiveFilters && <Typography component="span" variant="caption" color="primary" fontWeight={600}>Filtros ativos</Typography>}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: '0.74rem' }}>
              {isLoading ? <Skeleton width={100} sx={{ display: 'inline-block' }} /> : (
                <><strong>{fmtInt(data.length)}</strong> registro(s) encontrado(s)</>
              )}
            </Typography>
          </Box>
          <IconButton size="small">{filtersOpen ? <ExpandLess /> : <ExpandMore />}</IconButton>
        </Box>
        <Collapse in={filtersOpen}>
          <Box sx={{ p: 1, pt: 0.5 }}>
            <Grid container spacing={1.5} sx={{ mt: 0 }}>
              <Grid item xs={6} sm={6} md={2.4}>
                <SysDateRangePicker label="Data do Contrato" value={{ startDate: filters.dataInicio ? parseYmdToLocalDate(filters.dataInicio) : null, endDate: filters.dataFim ? parseYmdToLocalDate(filters.dataFim) : null }} onChange={(range) => onFilterChange({ dataInicio: dateToYmd(range.startDate), dataFim: dateToYmd(range.endDate) })} />
              </Grid>
              <Grid item xs={6} sm={6} md={2.4}>
                <SysDateRangePicker label="Data de Bloqueio" value={{ startDate: filters.dataBloqueioInicio ? parseYmdToLocalDate(filters.dataBloqueioInicio) : null, endDate: filters.dataBloqueioFim ? parseYmdToLocalDate(filters.dataBloqueioFim) : null }} onChange={(range) => onFilterChange({ dataBloqueioInicio: dateToYmd(range.startDate), dataBloqueioFim: dateToYmd(range.endDate) })} />
              </Grid>
              <Grid item xs={6} sm={6} md={2.4}>
                <SysDateRangePicker label="Data de Instalação" value={{ startDate: filters.dataInstalacaoInicio ? parseYmdToLocalDate(filters.dataInstalacaoInicio) : null, endDate: filters.dataInstalacaoFim ? parseYmdToLocalDate(filters.dataInstalacaoFim) : null }} onChange={(range) => onFilterChange({ dataInstalacaoInicio: dateToYmd(range.startDate), dataInstalacaoFim: dateToYmd(range.endDate) })} />
              </Grid>
              <Grid item xs={6} sm={6} md={2.4}>
                <SysDateRangePicker label="Data de Cancelamento" value={{ startDate: filters.dataCancelamentoInicio ? parseYmdToLocalDate(filters.dataCancelamentoInicio) : null, endDate: filters.dataCancelamentoFim ? parseYmdToLocalDate(filters.dataCancelamentoFim) : null }} onChange={(range) => onFilterChange({ dataCancelamentoInicio: dateToYmd(range.startDate), dataCancelamentoFim: dateToYmd(range.endDate) })} />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <SysDateRangePicker label="Desbloqueio Confiança" value={{ startDate: filters.desbloqueioConfiancaInicio ? parseYmdToLocalDate(filters.desbloqueioConfiancaInicio) : null, endDate: filters.desbloqueioConfiancaFim ? parseYmdToLocalDate(filters.desbloqueioConfiancaFim) : null }} onChange={(range) => onFilterChange({ desbloqueioConfiancaInicio: dateToYmd(range.startDate), desbloqueioConfiancaFim: dateToYmd(range.endDate) })} />
              </Grid>
              <Grid item xs={4} sm={3} md={1}>
                <TextField fullWidth size="small" select label="Estado" value={filters.estado} onChange={(e) => onFilterChange({ estado: e.target.value })}>
                  <MenuItem value="">Todos</MenuItem>
                  {estadosBR.map((uf) => <MenuItem key={uf} value={uf}>{uf}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={8} sm={3} md={2.5}>
                <TextField fullWidth size="small" label="Cidade" value={filters.cidade} onChange={(e) => onFilterChange({ cidade: e.target.value })} />
              </Grid>
              <Grid item xs={6} sm={3} md={2.5}>
                <TextField fullWidth size="small" label="Bairro" value={filters.bairro} onChange={(e) => onFilterChange({ bairro: e.target.value })} />
              </Grid>
              <Grid item xs={6} sm={3} md={2.5}>
                <FuncionariosSelect
                  value={filters.vendedor ? Number(filters.vendedor) : null}
                  onChange={(id) => onFilterChange({ vendedor: id != null ? String(id) : '' })}
                  label="Vendedor"
                  size="small"
                  placeholder="Todos"
                  allowClear={true}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField fullWidth size="small" select label="Status Contrato" value={filters.statusContrato} onChange={(e) => onFilterChange({ statusContrato: e.target.value })}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="ativo">Ativo</MenuItem>
                  <MenuItem value="bloqueado">Bloqueado</MenuItem>
                  <MenuItem value="cancelado">Cancelado</MenuItem>
                  <MenuItem value="aguardando_instalacao">Ag. Instalação</MenuItem>
                  <MenuItem value="em_dias">Em dias</MenuItem>
                  <MenuItem value="sem_faturas">Sem Faturas</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3} md={1.5} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={filters.inadimplente}
                      onChange={(e) => onFilterChange({ inadimplente: e.target.checked })}
                      color="error"
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontSize: '0.8rem', color: filters.inadimplente ? 'error.main' : 'text.primary', fontWeight: filters.inadimplente ? 700 : 400 }}>Apenas Inadimplentes</Typography>}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={6}>
                <TextField fullWidth size="small" placeholder="Buscar por título, ID, cliente..." value={filters.search} onChange={(e) => onFilterChange({ search: e.target.value })} 
                InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>, endAdornment: filters.search ? <InputAdornment position="end"><IconButton size="small" onClick={() => onFilterChange({ search: '' })}><Clear sx={{ fontSize: 16 }} /></IconButton></InputAdornment> : null }} />
              </Grid>

              <Grid item xs={6} sm={3} md={3}>
                <PlanosSelect
                  value={filters.plano ? Number(filters.plano) : null}
                  onChange={(id) => onFilterChange({ plano: id != null ? String(id) : '' })}
                  label="Plano"
                  size="small"
                  placeholder="Todos"
                  allowClear={true}
                />
              </Grid>
              <Grid gap={1} item xs={6} sm={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={exportingPdf ? null : <PictureAsPdf sx={{ fontSize: 16 }} />}
                  onClick={handleExportPdf}
                  disabled={isLoading || data.length === 0 || exportingPdf}
                  sx={{ textTransform: 'none', fontSize: '0.78rem' }}
                >
                  {exportingPdf ? 'Exportando...' : 'Exportar PDF'}
                </Button>
                <Button size="small" variant="outlined" startIcon={<Clear sx={{ fontSize: 16 }} />} onClick={onClearFilters} disabled={!hasActiveFilters} sx={{ textTransform: 'none', fontSize: '0.78rem' }}>Limpar Filtros</Button>
              </Grid>

            </Grid>
            
          </Box>
        </Collapse>
      </Paper>

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (theme) => `color-mix(in srgb, ${cores.ativo} 12%, ${theme.palette.background.paper})`,
                  fontWeight: 700,
                  fontSize: '0.73rem',
                  py: 0.8,
                  borderBottom: `2px solid ${alpha(cores.ativo, 0.2)}`,
                },
              }}>
                {columns.map((col: any) => (
                  <TableCell key={col.id} sx={{ width: col.width }}>
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? orderDir : 'asc'}
                      onClick={() => onSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {columns.map((col: any) => (
                      <TableCell key={col.id}><Skeleton /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.disabled">Nenhum registro encontrado</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((c: any) => (
                  <TableRow
                    key={c.id}
                    hover
                    onClick={() => navigate(`/contracts/${c.id}`)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(cores.ativo, 0.03) } }}
                  >
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>#{c.id}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem', fontWeight: 600 }}>{c.titulo}</TableCell>
                    <TableCell
                      sx={{
                        py: 0.5,
                        fontSize: '0.78rem',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/clients/${c.cliente_id}`);
                      }}
                    >
                      {c.cliente?.nome || `#${c.cliente_id}`}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{c.vendedor?.nome || '-'}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{getStatusChip(c.status, cores)}</TableCell>
                    {mostrarColunaQtdeFaturas && (
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem', textAlign: 'center' }}>{c.qtde_faturas_vencidas ?? 0}</TableCell>
                    )}
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{c.cidade_inst}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{c.estado_inst}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{formatarData(c.data_contrato)}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem', textAlign: 'center' }}>{c.dia_vencimento}</TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem', fontWeight: 600 }}>{formatarValor(c.valor_contratado || 0)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {data.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 0.8,
              px: 2,
              bgcolor: alpha(cores.ativo, 0.04),
              borderTop: `1px solid ${alpha(cores.ativo, 0.15)}`,
            }}
          >
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={data.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_e, p) => onPageChange(p)}
              onRowsPerPageChange={(e) => { onRowsPerPageChange(parseInt(e.target.value, 10)); onPageChange(0); }}
              labelRowsPerPage="Linhas por pág:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              sx={{
                width: '100%',
                '.MuiTablePagination-toolbar': {
                  minHeight: 32,
                  flexWrap: 'nowrap',
                  justifyContent: 'center',
                  gap: 1,
                  p: 0,
                  '& .MuiTablePagination-spacer': { display: 'none' },
                },
                '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.78rem' },
                '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.78rem' },
                '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.78rem' },
                '.MuiTablePagination-actions': { ml: 0.5, '& .MuiIconButton-root': { p: 0.5 } },
              }}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default RelatorioContratosTab;
